import React from "react";
import PropTypes from "prop-types";

import { Alert, Button, Card, Input, Radio, Select, Space, Spin, Tag, Typography } from "antd";

import {
    PAYMENT_METHOD_LABELS,
    PAYMENT_SERVICE_UNAVAILABLE_MESSAGE,
    unavailableReasonLabel
} from "../../../../../../constants/marketplaceCheckout";

const { Text, Title } = Typography;

/** Message explicite, ou null si les champs Mobile Money sont valides. */
export const mobileMoneyError = ({ operator, phone, phoneRule }) => {
    if (!operator) {
        return "Choisissez votre opérateur Mobile Money.";
    }
    const digits = (phone || "").replace(/[\s.-]/g, "");
    if (!digits) {
        return "Saisissez votre numéro Mobile Money.";
    }
    if (!/^\d+$/.test(digits)) {
        return "Le numéro ne doit contenir que des chiffres.";
    }
    if (phoneRule && digits.length !== phoneRule.nationalNumberLength) {
        return `Le numéro doit comporter ${phoneRule.nationalNumberLength} chiffres après ${phoneRule.dialCode}.`;
    }
    return null;
};

/**
 * Section « Paiement » : méthodes et opérateurs issus du catalogue backend
 * du pays d'activité. Aucune donnée de carte n'est saisie ici.
 */
const PaymentMethodSection = ({
    catalog,
    catalogLoading,
    catalogError,
    deliveryType,
    paymentMethod,
    onPaymentMethodChange,
    operator,
    onOperatorChange,
    phone,
    onPhoneChange,
    onRetryCatalog,
    disabled,
    title = "2. Paiement"
}) => {
    const methods = catalog?.methods ?? [];
    const mobile = methods.find(method => method.method === "MOBILE_MONEY");
    const operators = (mobile?.operators ?? []).filter(item => item.available);
    const phoneRule = catalog?.phone ?? null;
    const phoneError = paymentMethod === "MOBILE_MONEY" && phone
        ? mobileMoneyError({ operator, phone, phoneRule })
        : null;

    return (
        <Card>
            <Title level={5} style={{ marginTop: 0 }}>{title}</Title>
            {catalogLoading && <Spin description="Chargement des moyens de paiement..." />}
            {catalogError && (
                <Alert
                    type="error"
                    showIcon
                    title="Moyens de paiement indisponibles"
                    description={catalogError}
                    action={onRetryCatalog && <Button size="small" onClick={onRetryCatalog}>Réessayer</Button>}
                />
            )}
            {!catalogLoading && catalog && catalog.paymentServiceAvailable === false && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    title={PAYMENT_SERVICE_UNAVAILABLE_MESSAGE.title}
                    description={PAYMENT_SERVICE_UNAVAILABLE_MESSAGE.description}
                    action={onRetryCatalog && <Button size="small" onClick={onRetryCatalog}>Réessayer</Button>}
                />
            )}
            {!catalogLoading && catalog && deliveryType && catalog.paymentServiceAvailable !== false
                && !methods.some(method => method.available && method.allowedDeliveryTypes?.includes(deliveryType)) && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    title="Aucun moyen de paiement disponible pour ce mode de livraison"
                    description="Chaque moyen indisponible affiche sa raison ci-dessous."
                />
            )}

            {!catalogLoading && !catalogError && (
                <Radio.Group
                    value={paymentMethod}
                    disabled={disabled}
                    onChange={event => onPaymentMethodChange(event.target.value)}
                    style={{ width: "100%" }}
                >
                    <Space orientation="vertical" style={{ width: "100%" }}>
                        {methods.map(method => {
                            const incompatible = deliveryType && !method.allowedDeliveryTypes?.includes(deliveryType);
                            const labels = PAYMENT_METHOD_LABELS[method.method] || {};
                            return (
                                <Radio key={method.method} value={method.method} disabled={!method.available || incompatible}>
                                    <strong>{method.label || labels.title || method.method}</strong>
                                    {!method.available && (
                                        <Tag style={{ marginLeft: 8 }}>{unavailableReasonLabel(method.unavailableReason)}</Tag>
                                    )}
                                    {method.available && incompatible && (
                                        <Tag style={{ marginLeft: 8 }}>Uniquement avec le retrait au siège</Tag>
                                    )}
                                    {labels.description && (
                                        <>
                                            <br />
                                            <Text type="secondary">{labels.description}</Text>
                                        </>
                                    )}
                                </Radio>
                            );
                        })}
                    </Space>
                </Radio.Group>
            )}

            {paymentMethod === "MOBILE_MONEY" && (
                <Space orientation="vertical" style={{ width: "100%", marginTop: 16 }}>
                    {operators.length === 0 ? (
                        <Alert type="warning" showIcon title={unavailableReasonLabel(mobile?.unavailableReason)} />
                    ) : (
                        <>
                            <Text strong>Opérateur</Text>
                            <Select
                                placeholder="Choisissez votre opérateur"
                                value={operator}
                                disabled={disabled}
                                onChange={onOperatorChange}
                                options={operators.map(item => ({ value: item.code, label: item.label || item.code }))}
                            />
                            <Text strong>Numéro Mobile Money</Text>
                            <Space.Compact style={{ width: "100%" }}>
                                <Space.Addon>{phoneRule?.dialCode}</Space.Addon>
                                <Input
                                    inputMode="numeric"
                                    autoComplete="tel-national"
                                    placeholder={phoneRule ? `${phoneRule.nationalNumberLength} chiffres` : ""}
                                    value={phone}
                                    disabled={disabled}
                                    onChange={event => onPhoneChange(event.target.value)}
                                />
                            </Space.Compact>
                            {phoneError && <Text type="danger">{phoneError}</Text>}
                        </>
                    )}
                </Space>
            )}

            {(paymentMethod === "CARD" || paymentMethod === "PAYPAL") && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    title="Vous allez être redirigé vers une page de paiement sécurisée."
                />
            )}
        </Card>
    );
};

PaymentMethodSection.propTypes = {
    catalog: PropTypes.object,
    catalogLoading: PropTypes.bool,
    catalogError: PropTypes.string,
    deliveryType: PropTypes.string,
    paymentMethod: PropTypes.string,
    onPaymentMethodChange: PropTypes.func.isRequired,
    operator: PropTypes.string,
    onOperatorChange: PropTypes.func.isRequired,
    phone: PropTypes.string,
    onPhoneChange: PropTypes.func.isRequired,
    onRetryCatalog: PropTypes.func,
    disabled: PropTypes.bool,
    title: PropTypes.string
};

export default PaymentMethodSection;
